import {
  ICartProduct,
  IConditionAction,
  ITransformCartProduct,
  ITransformCartProductsParams,
} from '../../interfaces'
import { getActivePromotionsFields } from '../common/getPromotionFields'
import {
  getPromotionDetailsByType,
  promotionsMap,
} from './getPromotionDetailsByType'
import { validateAndTransformActivePromotion } from './validateAndTransformActivePromotion'

/**
 * Array of conditional transformations for cart products based on active promotions.
 * Each entry contains a `condition` function to determine if the transformation applies,
 * and a `transformAction` function to update the cart product if necessary.
 */
export const transformProductArray: IConditionAction<
  ITransformCartProduct,
  ICartProduct
>[] = [
  {
    /**
     * If the product is part of customerGetsItems and its package matches 
     * the promotion's customerGetsPackage, skip transformation.
     */
    condition: ({
      activePromotionFields: {
        fields: { customerGetsItems, customerGetsPackage },
      },
      cartProduct: { sku, package: packageData },
    }) => !(
      customerGetsItems?.includes(sku) && 
      Object.is(packageData?.name ?? null, customerGetsPackage)
    ),
    /**
     * Action: return the original cart product without changes.
     */
    transformAction: ({ cartProduct }) => cartProduct,
  },
  {
    /**
     * If there is an active promotion and a handler exists for this promotion type,
     * apply the handler to determine if the promotion should be applied.
     * If so, trigger `getPromotionDetailsByType` to update the cart product.
     */
    condition: ({
      activePromotionFields: {
        fields: { type, customerGetsQuantity },
      },
      cartProduct,
      activePromotion,
      config,
    }) =>
      !!activePromotion &&
      !!promotionsMap[type](!!customerGetsQuantity)({
        activePromotion,
        cartProduct,
        config,
      }),
    /**
     * Action: update the cart product with promotion details.
     */
    transformAction: getPromotionDetailsByType,
  },
]

/**
 * Function declaration to set promotions ui details to be shown on cart
 *
 * @internal
 * @param product - The cart product to be updated
 * @param categories - The categories
 * @returns IProduct updated with promotion ui details
 */
export const setPromotionUiDetailsCart = ({
  cartProducts,
  activePromotions,
  config,
}: ITransformCartProductsParams): ICartProduct[] => {
  const result = activePromotions?.reduce<ICartProduct[]>(
    (currentItems, activePromotion) => {
      const updatedActivePromotion = validateAndTransformActivePromotion({
        cartProducts,
        activePromotion,
      })
      const activePromotionFields = getActivePromotionsFields(
        updatedActivePromotion,
      )
      return currentItems.map(
        (cartProduct) =>
          transformProductArray
            .find(({ condition }) =>
              condition({
                activePromotionFields,
                cartProduct,
                activePromotion,
                config,
                activePromotions,
              }),
            )
            ?.transformAction({
              activePromotionFields,
              cartProduct,
              activePromotion,
              config,
              cartProducts: currentItems,
              activePromotions,
            }) || cartProduct,
      )
    },
    cartProducts,
  )
  return result
}
